import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Row, Col, Card, Button } from "react-bootstrap";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { ContentState, convertToRaw, EditorState } from "draft-js";

import { getCompanyInfo, deleteImageFromCompanyInfo, deleteCompanyInfo } from "../Store/Thunk";
import { ClearCompanyInfoState } from "../Store";
import { CompanyInfoApiUrls } from "../API/AdminCompanyInfoAPI";
import ApiHelper from "../../../../Helpers/ApiHelper";
import { folderEnum, GetValueByKey, imageTypeEnum } from "../../../../Helpers/EnumHelper";

import Overlay from "../../../SharedComponents/Overlay";
import { UpdateAlertModalData } from "../../../SharedComponents/GeneralModals/Store";
import ImageUploader from "./ImageUploader";
import useFilePreview from "./useFilePreview";
import AlertModal from "../../../SharedComponents/GeneralModals/Components/AlertModal";
import ContractSection from "../../../Contracts/Components/ContractSection";
import { ContractAudience } from "../../../Contracts/API/ContractAPI";

export default function AdminCompanyInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // --- Redux state seçiciler, optional chaining ile ---
  const isLoading = useSelector((s) => s.companyInfo?.isLoading);
  const companyInfo = useSelector((s) => s.companyInfo, shallowEqual);
  const token = useSelector((s) => s.auth.user?.token);
  const adminCompany = useSelector((s) => s.auth.user?.companyUser?.company, shallowEqual);
  const companyId = adminCompany?.companyId;
  const companyName = adminCompany?.companyName;

  // --- Local state ---
  const [alertData, setAlertData] = useState({
    isShown: false,
    header: "",
    text: "",
  });
  const [editorState, setEditorState] = useState();

  // --- Preview hook’ları başlatırken null-check ---
  const logoHook = useFilePreview(
    companyInfo?.logoPath ? ApiHelper.cloudFrontURL + folderEnum.logo + companyInfo.logoPath : ""
  );
  const iconHook = useFilePreview(
    companyInfo?.iconPath ? ApiHelper.cloudFrontURL + folderEnum.logo + companyInfo.iconPath : ""
  );

  // --- Şirket bilgisi çekme / yönlendirme ---
  useEffect(() => {
    if (!companyId) {
      navigate("/company");
      return;
    }
    dispatch(ClearCompanyInfoState());
    dispatch(getCompanyInfo({ companyId }));
  }, [companyId, dispatch, navigate]);

  // --- Mail içeriğini DraftJS’e initialize etme ---
  useEffect(() => {
    const html = companyInfo?.mailContent ?? "<p> Adaylarınıza gönderilecek mail metni içeriği giriniz.</p>";
    const contentBlock = htmlToDraft(html);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
    setEditorState(EditorState.createWithContent(contentState));
  }, [companyInfo?.mailContent]);

  // --- Form submit handler ---
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!companyId) return;

    const formData = new FormData();
    if (logoHook.file) formData.append("logo", logoHook.file);
    if (iconHook.file) formData.append("icon", iconHook.file);
    formData.append("companyId", companyId);
    formData.append("mailContent", draftToHtml(convertToRaw(editorState.getCurrentContent())));

    ApiHelper.xmlPostFormData(CompanyInfoApiUrls.upsertCompanyInfo, formData, token, (res) => {
      dispatch(getCompanyInfo({ companyId }));
      const { statusCode, message } = JSON.parse(res.response);
      if (statusCode !== 1) {
        UpdateAlertModalData({
          isShown: true,
          header: t("serverError"),
          text: message,
        });
      }
    });
  };

  // --- Resim silme handler’ı ---
  const handleDeleteImage = (type, imageName) => {
    if (!type || !imageName || !companyInfo?.id) return;
    const typeEnum = GetValueByKey(type, imageTypeEnum);
    dispatch(
      deleteImageFromCompanyInfo({
        type: typeEnum,
        imageName,
        companyInfoId: companyInfo.id,
      })
    );
  };

  // --- Eğer companyInfo gelmemişse, ya yükleniyordur ya da null döneriz ---
  if (!companyInfo) {
    return isLoading ? <Overlay /> : null;
  }

  return (
    <Form onSubmit={handleSubmit}>
      {isLoading && <Overlay />}
      <AlertModal
        isShown={alertData.isShown}
        text={alertData.text}
        header={alertData.header}
        hideFunction={() => setAlertData({ isShown: false, header: "", text: "" })}
      />

      <Row className="mx-0 mb-4">
        {/* --- Şirket başlığı --- */}
        <Col xs={12} md={4}>
          <Card
            className="default-card text-24 text-color-gray-dark fw-medium flex-center"
            style={{ padding: "10px", height: "100%" }}
          >
            <h2 style={{ textAlign: "center" }}>
              #{companyId} {companyName}
            </h2>
          </Card>
        </Col>

        {/* --- Logo yükleyici --- */}
        <Col xs={12} md={4}>
          <ImageUploader
            name="logo"
            label={t("uploadLogo")}
            size={{ width: 200, height: 60 }}
            apiPath="logo"
            typeEnumKey="CompanyLogo"
            existingPath={companyInfo.logoPath}
            onDelete={handleDeleteImage}
            hook={logoHook}
          />
        </Col>

        {/* --- Icon yükleyici --- */}
        <Col xs={12} md={4}>
          <ImageUploader
            name="icon"
            label={t("uploadIcon")}
            size={{ width: 50, height: 50 }}
            apiPath="logo"
            typeEnumKey="CompanyIcon"
            existingPath={companyInfo.iconPath}
            onDelete={handleDeleteImage}
            hook={iconHook}
          />
        </Col>
      </Row>

      {/* --- Mail içeriği editörü ve aksiyon butonları --- */}
      <Row className="mx-0">
        <Col>
          <Card className="default-card" style={{ padding: "25px" }}>
            <Row className="mb-3 flex-center">
              <Col xs="auto">
                <h5> {t("CompanyMailEditorLabel")}</h5>
              </Col>
              <Col>
                <Row xs="auto" className="justify-content-md-end">
                  <Col xs="auto">
                    <Button type="submit" className="button primary">
                      {t("saveChangesButtonLabel")}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="danger"
                      className="button secondary danger"
                      onClick={() => {
                        if (companyId) {
                          dispatch(deleteCompanyInfo({ id: companyId }));
                          dispatch(ClearCompanyInfoState());
                          logoHook.clear();
                          iconHook.clear();
                        }
                      }}
                    >
                      {t("deleteButtonLabel")}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Editor editorState={editorState} onEditorStateChange={setEditorState} />
          </Card>
        </Col>
      </Row>

      {/* Inside the form, but safe: contract components use only type="button" buttons and
          portal modals without <form> elements, so nothing here can submit this form. */}
      <Row className="mx-0">
        <Col>
          <ContractSection
            companyId={companyId}
            audience={ContractAudience.Candidate}
            canManage
            title={t("contractsForCandidates")}
            description={t("contractsForCandidatesHint")}
          />
          <ContractSection
            companyId={companyId}
            audience={ContractAudience.CompanyAdmin}
            canManage={false}
            title={t("contractsFromCusPro")}
            description={t("contractsFromCusProHint")}
          />
        </Col>
      </Row>
    </Form>
  );
}
